import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EventIcon from "@mui/icons-material/Event";
import { adjustTimes, getStopDepartureDatetime, getStopArrivalDatetime, getSegmentDurationMinutes } from "../utils/DateTime";
import { getCityDisplay, cityMatchesSearch } from "../utils/city";
import MapAddressLink from "./MapAddressLink";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TripCard = ({ trip, fromCity, toCity, adults, children, onSelect }) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const departureDateTime = dayjs(trip.departure_date);
  const stops = adjustTimes(departureDateTime, trip.route?.stops ?? []);

  const firstStopIndex = stops.findIndex((stop) => cityMatchesSearch(stop.city, fromCity));
  const lastStopIndex = stops.findIndex((stop) => cityMatchesSearch(stop.city, toCity));

  if (
    !stops.length ||
    firstStopIndex === -1 ||
    lastStopIndex === -1 ||
    firstStopIndex >= lastStopIndex
  ) {
    return null;
  }

  const firstStop = stops[firstStopIndex];
  const lastStop = stops[lastStopIndex];

  const segmentStart = getStopDepartureDatetime(firstStop);
  const segmentEnd = getStopArrivalDatetime(lastStop);

  if (!segmentStart || !segmentEnd) {
    return null;
  }

  const totalDurationMinutes = getSegmentDurationMinutes(firstStop, lastStop);
  if (totalDurationMinutes == null || totalDurationMinutes < 0) {
    return null;
  }
  const totalHours = Math.floor(totalDurationMinutes / 60);
  const totalMinutes = totalDurationMinutes % 60;
  const totalDurationText = `${String(totalHours).padStart(2, "0")}:${String(totalMinutes).padStart(2, "0")}`;

  const seatsLeft =
    Number.isFinite(Number(trip.free_seats)) && Number(trip.free_seats) <= 5
      ? t("tripCard.seatsLeft", { count: Number(trip.free_seats) })
      : "";

  const transfersCount = stops
    .slice(firstStopIndex + 1, lastStopIndex)
    .filter((item) => item.by_transfer).length;

  const listPriceUah = trip.fare?.list_price_uah;
  const campaignPriceUah = trip.fare?.price_uah;
  const hasCampaign =
    campaignPriceUah != null &&
    listPriceUah != null &&
    Number(campaignPriceUah) < Number(listPriceUah);
  const priceUah = hasCampaign
    ? Number(campaignPriceUah)
    : (trip.fare?.price ?? 0) * (trip.currency_rate ?? 1);
  const campaignLabel = trip.fare?.campaign_label;
  const savingsUah = hasCampaign
    ? Math.round(Number(listPriceUah) - Number(campaignPriceUah))
    : 0;
  const discountPercent = hasCampaign && Number(listPriceUah) > 0
    ? ((Number(listPriceUah) - Number(campaignPriceUah)) / Number(listPriceUah)) * 100
    : 0;
  const roundedPercent = Math.round(discountPercent);
  const cleanPercent = roundedPercent > 0 && Math.abs(discountPercent - roundedPercent) < 0.05;
  const badgeLabel = !hasCampaign
    ? ""
    : campaignLabel
      ? campaignLabel
      : cleanPercent
        ? t("tripCard.badgePercent", { percent: roundedPercent })
        : t("tripCard.badgeAmount", { amount: savingsUah });
  const bookableTo = trip.fare?.campaign_bookable_to;
  const offerDate =
    bookableTo && dayjs(bookableTo).endOf("day").isAfter(dayjs())
      ? dayjs(bookableTo).format(
          dayjs(bookableTo).year() === dayjs().year() ? "DD.MM" : "DD.MM.YYYY"
        )
      : "";
  const payOnline = Boolean(hasCampaign && trip.fare?.campaign_online_only);
  const offerLine =
    payOnline && offerDate
      ? t("tripCard.payOnlineUntil", { date: offerDate })
      : payOnline
        ? t("tripCard.payOnline")
        : offerDate
          ? t("tripCard.until", { date: offerDate })
          : "";

  const handleSelect = () => {
    if (onSelect) {
      onSelect({ trip, fromCity, toCity, adults, children });
      return;
    }
    navigate("/checkout", {
      state: { trip, fromCity, toCity, adults, children },
    });
  };

  return (
    <Card
      component="article"
      aria-label={t("tripCard.ariaLabel", { time: segmentStart.format("HH:mm"), endTime: segmentEnd.format("HH:mm"), price: priceUah })}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          boxShadow: theme.shadows[4],
          borderColor: theme.palette.primary.light,
        },
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          "&:last-child": { pb: { xs: 2, sm: 3 } },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 2.5, sm: 3 },
          }}
        >
          {/* Відправлення */}
          <Box sx={{ flex: { sm: 1 }, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                component="span"
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {segmentStart.format("HH:mm")}
              </Typography>
              <Chip
                size="small"
                icon={<EventIcon sx={{ fontSize: 14 }} />}
                label={segmentStart.format("DD.MM.YYYY")}
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  bgcolor: "action.hover",
                  "& .MuiChip-icon": { color: "primary.main" },
                }}
              />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
              {getCityDisplay(firstStop.city)}
            </Typography>
            {firstStop.address && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                <MapAddressLink city={getCityDisplay(firstStop.city)} address={firstStop.address} />
              </Typography>
            )}
          </Box>

          {/* Маршрут: час + іконка */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: { sm: "0 0 auto" },
              width: { xs: "100%", sm: 140 },
            }}
          >
            <Chip
              size="small"
              icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
              label={totalDurationText}
              sx={{
                mb: 1,
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "& .MuiChip-icon": { color: "primary.contrastText" },
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  bgcolor: "divider",
                }}
              />
              <DirectionsBusIcon
                sx={{ color: "primary.main", fontSize: 28 }}
                aria-hidden
              />
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  borderRadius: 1,
                  bgcolor: "divider",
                }}
              />
            </Box>
            {transfersCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("tripCard.transfers", { count: transfersCount })}
              </Typography>
            )}
          </Box>

          {/* Прибуття */}
          <Box sx={{ flex: { sm: 1 }, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography
                component="span"
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {segmentEnd.format("HH:mm")}
              </Typography>
              <Chip
                size="small"
                icon={<EventIcon sx={{ fontSize: 14 }} />}
                label={segmentEnd.format("DD.MM.YYYY")}
                sx={{
                  height: 24,
                  fontSize: "0.75rem",
                  bgcolor: "action.hover",
                  "& .MuiChip-icon": { color: "primary.main" },
                }}
              />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 0.5 }}>
              {getCityDisplay(lastStop.city)}
            </Typography>
            {lastStop.address && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                <MapAddressLink city={getCityDisplay(lastStop.city)} address={lastStop.address} />
              </Typography>
            )}
          </Box>

          {/* Ціна та дія */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "flex-start", sm: "flex-end" },
              justifyContent: "center",
              flex: { sm: "0 0 auto" },
              minWidth: { sm: 210 },
            }}
          >
            {badgeLabel && (
              <Chip
                label={badgeLabel}
                size="small"
                sx={{
                  mb: 0.75,
                  maxWidth: "100%",
                  height: "auto",
                  alignSelf: { xs: "flex-start", sm: "flex-end" },
                  fontWeight: 700,
                  bgcolor: "error.main",
                  color: "error.contrastText",
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    textAlign: { xs: "left", sm: "right" },
                    py: 0.5,
                  },
                }}
              />
            )}
            {hasCampaign && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  textDecoration: "line-through",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {t("units.uahAmount", { amount: Math.round(Number(listPriceUah)) })}
              </Typography>
            )}
            <Typography
              variant="h5"
              sx={{
                color: "error.main",
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1.2,
              }}
            >
              {t("units.uahAmount", { amount: Math.round(priceUah) })}
            </Typography>
            {offerLine && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block", textAlign: { xs: "left", sm: "right" } }}
              >
                {offerLine}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleSelect}
              fullWidth={isMobile}
              sx={{
                mt: 1.5,
                minWidth: 140,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                py: 1,
              }}
            >
              {t("trips.select")}
            </Button>
            {seatsLeft && (
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", fontWeight: 600, color: "error.main" }}
              >
                {seatsLeft}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TripCard;
